import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, type AIContentType, type AIOverrideMeta } from "../api";
import { Card, SectionHeading, Spinner } from "./ui";
import { SectionAnnotations } from "./SectionAnnotations";

type Status = "cached" | "generating" | "empty" | "overridden";

interface CachedResponse<T> {
  content: { type: AIContentType; data: T; generatedAt: string } | null;
  status: Status;
  override?: AIOverrideMeta;
  hasOriginal?: boolean;
}

interface GenerateResponse<T> {
  content: { type: AIContentType; data: T; generatedAt: string };
  cached: boolean;
}

export interface CustomEditorProps<T> {
  bookId: number;
  type: AIContentType;
  initial: T | undefined;
  onClose: () => void;
  onSaved: () => void;
}

interface Props<T> {
  bookId: number;
  type: AIContentType;
  eyebrow: string;
  title: string;
  description?: string;
  emptyCta: string;
  render: (data: T) => ReactNode;
  // Optional form-based editor — when omitted, the section falls back to the
  // generic JSON editor. Use this for sections with a small, well-known shape
  // (like venue) so members don't have to hand-edit JSON.
  customEditor?: (props: CustomEditorProps<T>) => ReactNode;
}

export function AISection<T>({
  bookId,
  type,
  eyebrow,
  title,
  description,
  emptyCta,
  render,
  customEditor,
}: Props<T>) {
  const qc = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const cache = useQuery({
    queryKey: ["ai", bookId, type],
    queryFn: () => api.get<CachedResponse<T>>(`/ai/${bookId}/${type}`),
    refetchInterval: (query) => {
      const d = query.state.data as CachedResponse<T> | undefined;
      if (!d) return 4_000;
      return d.status === "generating" ? 4_000 : false;
    },
  });

  const generate = useMutation({
    mutationFn: (force: boolean) =>
      api.post<GenerateResponse<T>>(`/ai/${bookId}/${type}/generate${force ? "?force=1" : ""}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai", bookId, type] }),
  });

  const resetOverride = useMutation({
    mutationFn: () => api.del(`/annotations/${bookId}/${type}/override`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ai", bookId, type] }),
  });

  const data = cache.data?.content?.data;
  const status: Status = cache.data?.status ?? "empty";
  const override = cache.data?.override;
  const isFirstLoading = cache.isLoading;
  const isManualGenerating = generate.isPending;
  const isAutoGenerating = !data && status === "generating";
  const error = generate.error;

  return (
    <Card accent>
      <div className="flex items-start justify-between gap-4 mb-6">
        <SectionHeading eyebrow={eyebrow} title={title} description={description} />
        {data && !isManualGenerating ? (
          <div className="flex items-center gap-2 shrink-0">
            {customEditor ? (
              <button
                onClick={() => setEditorOpen(true)}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-bordeaux border border-bordeaux px-3 py-1.5 hover:bg-bordeaux hover:text-rose-paper transition-colors"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M4 20h4l10-10-4-4L4 16v4z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M14 6l4 4" stroke="currentColor" strokeWidth="1.6" />
                </svg>
                Edit
              </button>
            ) : null}
            <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="text-xs uppercase tracking-[0.16em] text-ink-mute hover:text-bordeaux transition-colors px-3 py-1.5 hover:bg-rose-paper-2/50"
              aria-label="Section options"
            >
              ⋯
            </button>
            {menuOpen ? (
              <div className="absolute right-0 top-full mt-1 z-10 bg-white rounded-lg shadow-card border border-cream-200 py-1 min-w-[200px]">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    generate.mutate(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-plum hover:bg-cream-100"
                >
                  Regenerate with AI
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setEditorOpen(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-plum hover:bg-cream-100"
                >
                  Edit this section
                </button>
                {status === "overridden" ? (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      if (confirm("Reset to the original AI version? Your edit will be discarded.")) {
                        resetOverride.mutate();
                      }
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-plum hover:bg-cream-100 border-t border-cream-100"
                  >
                    Reset to AI version
                  </button>
                ) : null}
              </div>
            ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {status === "overridden" && override ? (
        <div className="mb-5 px-4 py-3 rounded-xl bg-gold/10 border border-gold/30 text-sm flex flex-col gap-1">
          <div className="text-[0.65rem] uppercase tracking-[0.18em] text-gold-dark">
            Hand-edited by{" "}
            {override.editor?.name ?? override.editor?.email.split("@")[0] ?? "a member"}
          </div>
          {override.reason ? (
            <p className="text-plum-soft italic">"{override.reason}"</p>
          ) : null}
        </div>
      ) : null}

      {isFirstLoading ? (
        <div className="flex items-center gap-3 text-plum-soft">
          <Spinner /> Loading…
        </div>
      ) : isManualGenerating || isAutoGenerating ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="text-rose mb-4">
            <Spinner size={28} />
          </div>
          <p className="text-plum-soft italic">
            {isAutoGenerating
              ? "Claude is on it — this will appear in a moment…"
              : "Asking Claude to think this through…"}
          </p>
        </div>
      ) : data ? (
        <div className="text-plum">{render(data)}</div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-plum-soft mb-5 max-w-md">
            Nothing here yet. Tap below to have Claude generate this section for you.
          </p>
          <button
            onClick={() => generate.mutate(false)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blush-soft text-rose-dark font-medium text-sm hover:bg-blush transition-colors"
          >
            <SparkleIcon />
            {emptyCta}
          </button>
        </div>
      )}

      {error ? (
        <p className="mt-4 text-sm text-rose-dark text-center">{(error as Error).message}</p>
      ) : null}

      {data ? <SectionAnnotations bookId={bookId} type={type} /> : null}

      {editorOpen
        ? customEditor
          ? customEditor({
              bookId,
              type,
              initial: data,
              onClose: () => setEditorOpen(false),
              onSaved: () => {
                setEditorOpen(false);
                qc.invalidateQueries({ queryKey: ["ai", bookId, type] });
              },
            })
          : (
            <OverrideEditor
              bookId={bookId}
              type={type}
              initial={data}
              onClose={() => setEditorOpen(false)}
              onSaved={() => {
                setEditorOpen(false);
                qc.invalidateQueries({ queryKey: ["ai", bookId, type] });
              }}
            />
          )
        : null}
    </Card>
  );
}

function OverrideEditor<T>({
  bookId,
  type,
  initial,
  onClose,
  onSaved,
}: {
  bookId: number;
  type: AIContentType;
  initial: T | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(initial ?? null, null, 2));
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const save = useMutation({
    mutationFn: () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (err) {
        throw new Error("Invalid JSON: " + (err as Error).message);
      }
      return api.post(`/annotations/${bookId}/${type}/override`, {
        data: parsed,
        reason: reason.trim() || null,
      });
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 bg-plum/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-cream-50 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-cream-200">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-gold mb-0.5">
              Editing
            </div>
            <h3 className="font-serif text-xl text-plum capitalize">{type}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-plum-soft hover:text-rose p-2 -mr-2 rounded-full hover:bg-cream-100"
            aria-label="Close editor"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4 overflow-y-auto">
          <p className="text-sm text-plum-soft leading-relaxed">
            Edit the JSON below. This replaces what Claude generated; the AI version is kept and you can
            reset to it anytime.
          </p>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
            }}
            spellCheck={false}
            className="w-full min-h-[280px] px-4 py-3 rounded-xl bg-white border border-cream-300 text-plum text-sm font-mono leading-relaxed resize-y focus:outline-none focus:border-rose"
          />
          <div>
            <label className="text-xs font-medium uppercase tracking-[0.12em] text-plum-soft block mb-1.5">
              Why this edit? (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. fixed chapter titles, swapped wrong character name"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-cream-300 text-plum text-sm placeholder:text-plum-mute placeholder:italic focus:outline-none focus:border-rose"
            />
          </div>
          {error ? <p className="text-sm text-rose-dark">{error}</p> : null}
        </div>

        <div className="p-5 border-t border-cream-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-plum-soft hover:text-plum"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="px-5 py-2 rounded-xl bg-rose text-white text-sm hover:bg-rose-dark disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save override"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SparkleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5L12 3z"
        fill="currentColor"
        opacity="0.9"
      />
      <path d="M19 4l1 2.5L22.5 7.5 20 8.5 19 11l-1-2.5L15.5 7.5 18 6.5 19 4z" fill="currentColor" opacity="0.6" />
    </svg>
  );
}
